import { ADDRESS_REASON_CODE_MESSAGES, AddressReasonCode } from "common/constants/address";
import { AddressWriteMutation, IAddressFormValues, IVerifyAddress } from "common/types/addresses";

/**
 * What the form should do given a verify response. Two outcomes:
 *
 *   1. `autoSave` — Lob's cleaned address matches the user's input and is
 *      deliverable; persist immediately, no confirmation step.
 *   2. `confirm` — Lob wants user confirmation (typo/casing, or undeliverable).
 *      The picker stackable opens; if the address is undeliverable, the picker
 *      hands off to the `AddressUndeliverable{Dialog,Modal}` for the user to
 *      either edit (closes the stack back to the form) or override.
 */
export type VerifyOutcome =
  | { kind: "autoSave"; body: AddressWriteMutation }
  | { kind: "confirm"; data: IVerifyAddress };

export function classifyVerifyResponse(
  response: IVerifyAddress,
  values: IAddressFormValues,
): VerifyOutcome {
  if (!response.needs_confirmation) {
    return {
      kind: "autoSave",
      body: {
        ...values,
        cache_key: response.cache_key,
        use_cached_address: true,
      },
    };
  }
  return { kind: "confirm", data: response };
}

/**
 * Look up the user-facing message for a verify failure. Returns null when the
 * verify succeeded (no reason code). Callers pair this with `verifyError.field`
 * to decide whether to render the message inline or as a form-level alert.
 */
export function buildVerifyError(reasonCode: AddressReasonCode | null): string | null {
  if (!reasonCode) {
    return null;
  }
  return ADDRESS_REASON_CODE_MESSAGES[reasonCode];
}
